import React, { useEffect, useState } from 'react'

import {
  FadeSwap,
  HeadingMedium,
  HeadingLarge,
  BodyText,
  Button,
  IntersectionVisible,
  ProductCarousel,
  Loader,
  MessagePanel,
  ProductFilterBar,
  CTALink,
  ArrowLeftIcon,
  ArrowRightIcon,
  ProductGrid,
  CheckBoxInput,
  FilterTags,
} from '@pb/components'
import {
  trackViewEvent,
  isAppDevice,
  useRequest,
  getLabelFromStorage,
  trackEvent,
  getLocale,
  getArticleFromList,
} from '@pb/utils'
import { useFeature } from '@optimizely/react-sdk'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { EXTERNAL_ROUTES } from 'App/constants'
import appSelectors from 'App/duck/selectors'
// TODO clean up optimizely events https://hmgroup.atlassian.net/browse/BTSO-2109
import optimizelyConstants, {
  CLOSET_DETAIL_EXITS,
  CLICK_MY_CLOSET_PRODUCTS,
} from 'constants/optimizely'
import { myclosetPrepData } from 'features/favourites/duck/normalize'
import { trackOptimizelyEvent } from 'helpers/optimizely'
import triggerExternalModal, { signin } from 'helpers/triggerExternalModal'

import MyClosetDetail from './components/MyClosetDetail'
import styles from './MyCloset.scss'
import { MYCLOSET_TRACKING_EVENTS } from './tracking'
import { CategoriesResult, ArticleResult, FilterItem, FilterTagsValue, FilterLabels } from './types'

const NUM_OF_ITEMS_PER_SLIDE = {
  xsmall: 3,
  small: 3,
  medium: 3,
  large: 4,
  xlarge: 5,
  xxl: 5,
}

const MyCloset: React.FC = () => {
  const isSignedIn = useSelector(appSelectors.getIsSignedIn)
  const userBpid = useSelector(appSelectors.getUserBpid)
  const firstName = useSelector(appSelectors.getUserFirstName)

  const [shownCarousels, setShownCarousels] = useState({})
  const [articlesData, setData] = useState<ArticleResult>([])
  const [categoriesData, setCategories] = useState<CategoriesResult>([])
  const [isBannerClosed, setIsBannerClosed] = useState<boolean>(false)
  const [isFilterSelected, setisFilterSelected] = useState<boolean>(false)
  const [isCategorySubview, setCategorySubview] = useState<boolean>(false)
  const [filteredarticlesData, setFilteredData] = useState<ArticleResult>([])
  const [filteredCategoriesData, setFilteredCategories] = useState<CategoriesResult>([])
  const [filterTagsValue, setFilterTagsValue] = useState<FilterTagsValue>([])
  const [showMyclosetDetail, setShowMyclosetDetail] = useState<boolean>(false)
  const [productClicked, setproductClicked] = useState<string>('')
  const [productCategoryClicked, setproductCategoryClicked] = useState<string>('')
  const [showWelcomeBanner, setshowWelcomeBanner] = useState<boolean>(false)
  const { search } = useLocation()

  const queryParams = new URLSearchParams(search)
  const myClosetPill = queryParams.get('pill')

  const [isMyClosetDetail, { showMyClosetDetail }] = useFeature(optimizelyConstants.MY_CLOSET_FLAG)
  const showMyclosetDetailFlag =
    (isMyClosetDetail && showMyClosetDetail) ||
    showMyClosetDetail === localStorage.getItem('showMyClosetDetail')

  const isMyClosetDetailEnabled =
    process.env.IS_MY_CLOSET_DETAIL_ENABLED === 'enabled' && showMyclosetDetailFlag

  const {
    isRequestMade,
    loading: isLoadingResult,
    data: myclosetResult,
    execute: fetchClosetData,
  } = useRequest(`${process.env.MY_CLOSET_URL}/${getLocale('lowerCase')}/${userBpid}`, {
    manual: true,
    method: 'get',
  })

  useEffect(() => {
    if (!isRequestMade && isSignedIn) {
      fetchClosetData()
    }
  }, [])

  useEffect(() => {
    if (!isLoadingResult && myclosetResult && Object.keys(myclosetResult).length) {
      const prepData = myclosetPrepData(myclosetResult, isMyClosetDetailEnabled)
      setData(prepData?.flat().map((e) => e.flat()))
      const prepCategories = [myclosetResult].flat().map((result) =>
        result.mainCategories.map((subcategory) => ({
          category: subcategory.name,
          count: subcategory.articleCount,
        })),
      )
      setCategories(prepCategories?.flat())
    }
  }, [myclosetResult, isLoadingResult])

  useEffect(() => {
    if (filteredCategoriesData?.length) {
      const filterArticles = filteredCategoriesData.map((filter) => {
        const current = categoriesData.findIndex((cat) => cat.category === filter.category)
        return articlesData[current]
      })
      setFilteredData(filterArticles)
      if (!isCategorySubview) {
        setisFilterSelected(true)
        const tagsFilter = filteredCategoriesData.map((filter) => ({
          id: filter.category,
        }))
        setFilterTagsValue(tagsFilter)
      }
    } else {
      setisFilterSelected(false)
      setFilteredData([])
      setFilterTagsValue([])
    }
  }, [filteredCategoriesData, isCategorySubview])

  let onClick
  let buttonLabel

  useEffect(() => {
    if (!isSignedIn) {
      trackViewEvent(MYCLOSET_TRACKING_EVENTS.myclosetLoginView)
    } else if (isSignedIn && !isLoadingResult && isRequestMade && myClosetPill === 'closet') {
      if (articlesData?.length) {
        trackViewEvent(MYCLOSET_TRACKING_EVENTS.myclosetViewWithItems)
        if (
          !isBannerClosed &&
          !isCategorySubview &&
          !localStorage.getItem('hmMyClosetFirsttimeUser')
        ) {
          setshowWelcomeBanner(true)
          trackViewEvent({
            ...MYCLOSET_TRACKING_EVENTS.welcomeBannerView,
            customer_bpid: userBpid,
            page_id: 'MY CLOSET',
          })
        }
      } else {
        trackViewEvent(MYCLOSET_TRACKING_EVENTS.BrowseNowView)
      }
    }
  }, [isSignedIn, articlesData, myClosetPill])

  if (!isSignedIn) {
    buttonLabel = getLabelFromStorage('mycloset.login.button.title')
    onClick = () => {
      if (isAppDevice()) {
        window.location.href = EXTERNAL_ROUTES.login
        trackEvent(MYCLOSET_TRACKING_EVENTS.clickOnLogin)
      } else {
        triggerExternalModal(signin)
        trackEvent(MYCLOSET_TRACKING_EVENTS.clickOnLogin)
      }
    }
  } else {
    buttonLabel = getLabelFromStorage('favorites.empty.browsing')
    onClick = () => {
      window.location.href = EXTERNAL_ROUTES.index
      trackEvent({
        ...MYCLOSET_TRACKING_EVENTS.clickStartBrowsing,
        customer_bpid: userBpid,
      })
    }
  }

  const renderSection = (copy1, copy2): React.ReactElement => {
    return (
      <section className={styles.myClosetContainerEmpty}>
        <HeadingMedium center Tag="h2" className={styles.logText}>
          {copy1}
        </HeadingMedium>
        <BodyText center className={styles.infoText}>
          {copy2}
        </BodyText>
        <Button primary fullWidth onClick={onClick} className={styles.button}>
          {buttonLabel}
        </Button>
      </section>
    )
  }

  const onMessageClose = (): void => {
    localStorage.setItem('hmMyClosetFirsttimeUser', userBpid)
    trackEvent({
      ...MYCLOSET_TRACKING_EVENTS.welcomeBannerclick,
      customer_bpid: userBpid,
    })
    return setIsBannerClosed(!isBannerClosed)
  }

  const WelcomeBanner = (): React.ReactElement => {
    return (
      <div className={styles.myClosetWelcomeBanner}>
        <MessagePanel
          title={getLabelFromStorage('mycloset.overview.welcome.title', {
            replacements: firstName,
          })}
          isClosable
          onMessageClose={() => onMessageClose()}
          closeIconSize="xsmall"
        >
          <BodyText>{getLabelFromStorage('mycloset.overview.welcome.description')}</BodyText>
        </MessagePanel>
      </div>
    )
  }

  const handleViewAllClick = (categoryClicked): void => {
    const selectedTarget = categoryClicked
    if (selectedTarget) {
      const selectedCategoryData = categoriesData.filter(
        (cat) => cat.category === selectedTarget && cat,
      )
      setCategorySubview(true)
      setisFilterSelected(false)
      setFilteredCategories(selectedCategoryData)
      trackEvent({
        ...MYCLOSET_TRACKING_EVENTS.clickViewallBtn,
        event_label: selectedTarget,
        customer_bpid: userBpid,
      })
      trackViewEvent({
        ...MYCLOSET_TRACKING_EVENTS.categorySubview,
        customer_bpid: userBpid,
        page_id: selectedTarget,
        category_id: `FAVOURITES_MY CLOSET`,
      })
    }
  }

  const handleProductClicked = (product): void => {
    if (product.articleCode) {
      setShowMyclosetDetail(true)
      setproductClicked(product.articleCode)
      setproductCategoryClicked(product.mainCategory)
      trackEvent({
        ...MYCLOSET_TRACKING_EVENTS.clickPDPCategorySubview,
        event_label: `${product.mainCategory} | ${product.articleCode}`,
        product_id: product.articleCode,
        product_name: product.title,
        product_category: product.category,
        page_id: product.mainCategory,
      })
    }
  }
  const renderListGridView = (articlesGrid): React.ReactElement => {
    const resultGridArticles = articlesGrid.flat().map((elementGrid) => ({
      ...elementGrid,
      shouldNotShowProductDetails: true,
    }))
    return <ProductGrid products={resultGridArticles} onProductClick={handleProductClicked} />
  }

  const handleBackButton = (): void => {
    setCategorySubview(false)
    setisFilterSelected(false)
    setFilteredCategories([])
    setFilterTagsValue([])
    setShowMyclosetDetail(false)
  }

  const handleClickOnProduct = (event, productSet, categoryLabel): void => {
    const clickedProduct = getArticleFromList(event, productSet)
    if (clickedProduct) {
      trackEvent({
        ...MYCLOSET_TRACKING_EVENTS.clickOnPDP,
        customer_bpid: userBpid,
        event_label: `${categoryLabel}|${clickedProduct.articleCode}`,
        product_category: clickedProduct.category,
        product_id: clickedProduct.articleCode,
        product_name: clickedProduct.title,
      })
      trackOptimizelyEvent(CLICK_MY_CLOSET_PRODUCTS)
    }
    if (isMyClosetDetailEnabled) {
      setShowMyclosetDetail(true)
      setproductClicked(clickedProduct.articleCode)
      setproductCategoryClicked(categoryLabel)
    }
  }

  const renderProducts = (articlesProp, categoriesProp): React.ReactElement => {
    return articlesProp.map((articles, index) => {
      const { category, count } = categoriesProp[index]
      const articlesCat = articles.map((elementArticle) => ({
        ...elementArticle,
        shouldNotShowProductDetails: true,
      }))
      return (
        <IntersectionVisible
          key={category}
          onShow={() => {
            setShownCarousels((state) => ({ ...state, [category]: true }))
          }}
          active={!shownCarousels[category]}
          options={{ threshold: 0.3 }}
        >
          {count > 5 && (
            <CTALink
              className={styles.viewAllBtn}
              Tag="button"
              iconElement={<ArrowRightIcon size="small" />}
              iconPosition="end"
              onClick={() => {
                handleViewAllClick(category)
              }}
            >
              {getLabelFromStorage('mycloset.overview.viewall.button.title')}
            </CTALink>
          )}
          <ProductCarousel
            title={`${category} (${count})`}
            titleClassName={styles.titleClassName}
            className={styles.productCarousel}
            products={articlesCat}
            labels={{
              next: getLabelFromStorage('general.next'),
              prev: getLabelFromStorage('general.previous'),
            }}
            numOfItemsPerSlide={NUM_OF_ITEMS_PER_SLIDE}
            linkClickCallback={(e) => handleClickOnProduct(e, articlesCat, category)}
          />
        </IntersectionVisible>
      )
    })
  }

  const renderCategorySubviewMenu = (): React.ReactElement => {
    return (
      <>
        <CTALink
          className={styles.backBtn}
          Tag="button"
          iconElement={<ArrowLeftIcon />}
          onClick={handleBackButton}
          fullWidth
        />
        <HeadingLarge className={styles.categoryTitle} center Tag="h2">
          {filteredCategoriesData[0].category}
        </HeadingLarge>
      </>
    )
  }

  const handleFilterChange = (checked, selectedCategory): void => {
    if (!checked) {
      const removeCategoryData = filteredCategoriesData?.filter(
        (filterCat) => filterCat.category !== selectedCategory.category,
      )
      setFilteredCategories(removeCategoryData)
    }
    const checkFilteredCaegory = filteredCategoriesData?.find(
      (filter) => filter.category === selectedCategory.category,
    )
    if (checked && !checkFilteredCaegory) {
      const filteredCategories = [...filteredCategoriesData, selectedCategory]
      setFilteredCategories(filteredCategories)
    }
  }

  const isCategoryChecked = (category): boolean => {
    const isChecked = filteredCategoriesData.find((filter) => filter.category === category)
    return !!isChecked
  }

  const createFilterItem = (categories): FilterItem => {
    return categories.map((cat) => {
      return {
        heading: cat.category,
        title: cat.category,
        id: cat.category,
        selected: false,
        count: cat.count,
        disabled: true,
        linkElement: (
          // TODO remove styling as part of BTSO-1708
          <div id={styles.filterItemWrapper}>
            <CheckBoxInput
              name={cat.category}
              value={cat.category}
              inputID={cat.category}
              checked={isCategoryChecked(cat.category)}
              className={styles.filterItem}
              containerClassName={styles.filterItemContainer}
              label={
                <>
                  <span className={styles.filterItemName}>{cat.category}</span>
                  <span className={styles.filterItemCount}>{cat.count}</span>
                </>
              }
              onChange={(e) => {
                handleFilterChange(e, cat)
              }}
            />
          </div>
        ),
      }
    })
  }

  const handleClearAllFilters = (): void => {
    setFilteredCategories([])
    setFilteredData([])
    setisFilterSelected(false)
    setFilterTagsValue([])
  }

  const handleRemoveTag = (e): void => {
    const updatedFilter = filteredCategoriesData.filter(
      (filterCategory) => filterCategory.category !== e.id,
    )
    setFilteredCategories(updatedFilter)
  }

  const createFilterLabels = (): FilterLabels => {
    return {
      allFiltersScreenReader: getLabelFromStorage('mycloset.overview.categories.title'),
      allFiltersLargeScreen: getLabelFromStorage('mycloset.overview.categories.title'),
      allFiltersLargeScreenConditional: getLabelFromStorage('mycloset.overview.categories.title'),
      allFiltersSmallScreen: getLabelFromStorage('mycloset.overview.categories.title'),
      dropdownTitle: '',

      drawerHeading: getLabelFromStorage('mycloset.overview.categories.title'),
      drawerClose: '',
      drawerDone: getLabelFromStorage('mycloset.filterandsort.done.button.title'),
      drawerClearAll: '',
      drawerClearNextLevel: '',
    }
  }

  const handleAllFilterClick = (): void => {
    trackEvent({
      ...MYCLOSET_TRACKING_EVENTS.clickCategoryFilter,
      customer_bpid: userBpid,
    })
  }

  const renderCategoryMenu = (): React.ReactElement => {
    return (
      <div className={styles.categoryMenu}>
        <ProductFilterBar
          shouldShowCategoryDropDown={false}
          categoryButtonText={getLabelFromStorage('mycloset.overview.categories.title')}
          filtersData={createFilterItem(categoriesData)}
          shouldShowImageTypeSwitch={false}
          shouldShowGridSizeSwitch={false}
          totalItemsText={
            isFilterSelected
              ? getLabelFromStorage('mycloset.overview.items', {
                  replacements: filteredarticlesData?.flat().length,
                })
              : getLabelFromStorage('mycloset.overview.items', {
                  replacements: articlesData?.flat().length,
                })
          }
          labels={createFilterLabels()}
          shouldShowClearAllButtons={false}
          shouldShowTotalItemText={false}
          onAllFiltersClick={handleAllFilterClick}
        />
        <FilterTags
          tagsList={filterTagsValue}
          clearAllLabel={getLabelFromStorage('mycloset.filterandsort.clearfilters.button.title')}
          onClearTag={handleRemoveTag}
          onClearAllTags={handleClearAllFilters}
        />
      </div>
    )
  }

  const handleToggle = (): void => {
    trackEvent({
      ...MYCLOSET_TRACKING_EVENTS.myclosetDetailCloseBtn,
      customer_bpid: userBpid,
    })
    trackOptimizelyEvent(CLOSET_DETAIL_EXITS)
    setShowMyclosetDetail(!showMyclosetDetail)
  }

  return (
    <FadeSwap flag={isLoadingResult} placeholder={<Loader data-testid="loader-test-id" />}>
      {isSignedIn ? (
        !isLoadingResult && articlesData?.length && categoriesData?.length ? (
          <>
            {showWelcomeBanner && WelcomeBanner()}
            {!isCategorySubview ? renderCategoryMenu() : renderCategorySubviewMenu()}
            {!isFilterSelected &&
              !isCategorySubview &&
              articlesData.length === categoriesData.length &&
              renderProducts(articlesData, categoriesData)}
            {isFilterSelected &&
              !isCategorySubview &&
              filteredarticlesData.length === filteredCategoriesData.length &&
              renderProducts(filteredarticlesData, filteredCategoriesData)}
            {!isFilterSelected && isCategorySubview && renderListGridView(filteredarticlesData)}
            {showMyclosetDetail && isMyClosetDetailEnabled && (
              <MyClosetDetail
                isOpen={showMyclosetDetail}
                articleCode={productClicked}
                onToggle={handleToggle}
                categoryName={productCategoryClicked}
              />
            )}
          </>
        ) : (
          renderSection(
            getLabelFromStorage('mycloset.empty.title'),
            getLabelFromStorage('mycloset.empty.description'),
          )
        )
      ) : (
        renderSection(
          getLabelFromStorage('mycloset.login.title'),
          getLabelFromStorage('mycloset.login.description'),
        )
      )}
    </FadeSwap>
  )
}

export default MyCloset
